/**
 * Host Global - Property Rental Management System
 * Property Owner - My Properties Page
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropertyOwnerFooter from "examples/PropertyOwnerFooter";

// Context
import { useAuth } from "context/authContext";

// API
import { propertyOwnerAPI } from "utils/api";
import { getStatusLabel } from "utils/statusLabels";

// Icons
import Icon from "@mui/material/Icon";

function MyProperties() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(true);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [disableReason, setDisableReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [detailsTab, setDetailsTab] = useState(0);
  const [pdfLightboxOpen, setPdfLightboxOpen] = useState(false);
  const [pdfLightboxUrl, setPdfLightboxUrl] = useState("");
  const [pdfLightboxTitle, setPdfLightboxTitle] = useState("");
  const [useGoogleViewer, setUseGoogleViewer] = useState(false);

  // Helper function to fix PDF URLs that were uploaded as images
  const fixPdfUrl = (url) => {
    if (!url) return url;

    // Ensure URL is a string
    const urlString = String(url);

    // Check if URL contains /image/upload/ and is a PDF
    if (
      urlString.includes("/image/upload/") &&
      (urlString.toLowerCase().includes(".pdf") || urlString.toLowerCase().endsWith(".pdf"))
    ) {
      // Replace /image/upload/ with /raw/upload/ to properly serve PDF files
      let fixedUrl = urlString.replace("/image/upload/", "/raw/upload/");

      // Ensure .pdf extension is in the URL
      if (!fixedUrl.toLowerCase().includes(".pdf")) {
        // Try to add .pdf extension before query parameters
        const [baseUrl, queryParams] = fixedUrl.split("?");
        fixedUrl = baseUrl + ".pdf";
        if (queryParams) {
          fixedUrl += "?" + queryParams;
        }
      }

      return fixedUrl;
    }

    // If it's already a raw URL or doesn't need fixing, ensure it has .pdf extension if it's a PDF
    if (urlString.toLowerCase().includes(".pdf") || urlString.includes("raw/upload/")) {
      return urlString;
    }

    return urlString;
  };

  // Helper to add Cloudinary download transformation
  const addDownloadTransformation = (url) => {
    if (!url || !url.includes("cloudinary.com")) return url;

    try {
      const urlObj = new URL(url);
      // Add attachment query parameter to force download
      urlObj.searchParams.set("attachment", "true");
      return urlObj.toString();
    } catch (error) {
      // If URL parsing fails, try string manipulation
      if (url.includes("?")) {
        return url + "&attachment=true";
      } else {
        return url + "?attachment=true";
      }
    }
  };

  // Open PDF in lightbox viewer
  const viewPdf = (url, title = "Document") => {
    if (!url) {
      console.error("PDF URL is empty");
      return;
    }

    console.log("Opening PDF:", url, "Title:", title);

    // Fix URL if needed
    let fixedUrl = fixPdfUrl(url);
    console.log("Fixed PDF URL:", fixedUrl);

    // For Cloudinary URLs, ensure they're properly formatted for embedding
    // Remove any attachment parameters that might interfere with viewing
    if (fixedUrl.includes("cloudinary.com")) {
      try {
        const urlObj = new URL(fixedUrl);
        urlObj.searchParams.delete("attachment");
        // Ensure resource_type is set to raw for PDFs
        if (!urlObj.pathname.includes("/raw/upload/")) {
          urlObj.pathname = urlObj.pathname.replace("/image/upload/", "/raw/upload/");
        }
        fixedUrl = urlObj.toString();
      } catch (e) {
        console.error("Error parsing PDF URL:", e);
        // If URL parsing fails, try manual string replacement
        fixedUrl = fixedUrl.replace("/image/upload/", "/raw/upload/");
        fixedUrl = fixedUrl.replace("&attachment=true", "").replace("?attachment=true", "");
      }
    }

    console.log("Final PDF URL for viewing:", fixedUrl);

    setPdfLightboxUrl(fixedUrl);
    setPdfLightboxTitle(title);
    setUseGoogleViewer(false); // Reset to try object tag first
    setPdfLightboxOpen(true);
  };

  // Check profile completeness
  useEffect(() => {
    const checkProfile = async () => {
      try {
        setCheckingProfile(true);
        const profileResponse = await propertyOwnerAPI.getProfile();
        const profile = profileResponse.data || {};

        // Check if profile is complete (has idCard, address, nationality, and bank details)
        const hasIdCard = profile.idCard && profile.idCard.url;
        const hasAddress = profile.address && profile.address.trim() !== "";
        const hasNationality = profile.nationality && profile.nationality.trim() !== "";
        const hasBankDetails =
          profile.bankDetails &&
          profile.bankDetails.bankName &&
          profile.bankDetails.accountNumber &&
          profile.bankDetails.accountHolderName;
        const isComplete = hasIdCard && hasAddress && hasNationality && hasBankDetails;

        setProfileComplete(isComplete);

        if (!isComplete) {
          setMessage({
            type: "warning",
            text: "Please complete your profile (ID Card, Address, Nationality, and Bank Details) before accessing your properties.",
          });
        }
      } catch (error) {
        console.error("Error checking profile:", error);
      } finally {
        setCheckingProfile(false);
      }
    };

    checkProfile();
  }, []);

  // Fetch properties only if profile is complete
  useEffect(() => {
    if (!profileComplete || checkingProfile) return;

    const fetchProperties = async () => {
      try {
        setLoading(true);
        const response = await propertyOwnerAPI.getMyProperties();
        setProperties(response.data || []);
      } catch (error) {
        console.error("Error fetching properties:", error);
        setMessage({
          type: "error",
          text: "Failed to load properties. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [profileComplete, checkingProfile]);

  // Handle disable request
  const handleRequestDisable = (property) => {
    setSelectedProperty(property);
    setDisableReason("");
    setDisableDialogOpen(true);
  };

  const handleSubmitDisableRequest = async () => {
    if (!selectedProperty) return;

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const response = await propertyOwnerAPI.requestDisableProperty(
        selectedProperty._id || selectedProperty.id,
        disableReason
      );

      if (response.message) {
        setMessage({
          type: "success",
          text: response.message,
        });

        // Update property in list
        setProperties((prev) =>
          prev.map((prop) =>
            prop._id === selectedProperty._id || prop.id === selectedProperty.id
              ? { ...prop, disableRequested: true, disableRequestReason: disableReason }
              : prop
          )
        );

        setDisableDialogOpen(false);
        setSelectedProperty(null);
        setDisableReason("");
      }
    } catch (error) {
      console.error("Error requesting disable:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to submit disable request. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    if (["Approved", "Active"].includes(status)) return "success";
    if (["Pending", "Request_Submission"].includes(status)) return "warning";
    if (
      [
        "Inspection",
        "First_Inspection",
        "Inspection_Offer",
        "Inspection_Completed",
        "Second_Inspection",
        "Preparation",
        "Contracting",
        "Legalization",
        "Photo_Shooting",
      ].includes(status)
    )
      return "info";
    if (status === "Rejected") return "error";
    if (status === "Disabled") return "dark";
    return "secondary";
  };

  // Show loading while checking profile
  if (checkingProfile) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <PropertyOwnerFooter />
      </DashboardLayout>
    );
  }

  // Block access if profile is incomplete
  if (!profileComplete) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <MDBox
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "200px",
                height: "200px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                transform: "translate(50%, -50%)",
              }}
            />
            <MDBox p={4} position="relative" textAlign="center">
              <MDBox
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  p: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Icon sx={{ color: "white", fontSize: "3rem" }}>lock</Icon>
              </MDBox>
              <MDTypography variant="h4" fontWeight="bold" color="white" mb={2}>
                Profile Incomplete
              </MDTypography>
              <MDTypography
                variant="body1"
                color="white"
                sx={{ opacity: 0.9, mb: 3, maxWidth: "600px", mx: "auto" }}
              >
                To access your properties and begin listing, please complete your profile by adding
                your ID card, address, and nationality.
              </MDTypography>
              <MDBox display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <MDButton
                  variant="contained"
                  color="white"
                  size="large"
                  onClick={() => navigate("/profile")}
                  sx={{
                    backgroundColor: "white",
                    color: "#667eea",
                    fontWeight: "bold",
                    px: 4,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <Icon sx={{ mr: 1 }}>edit</Icon>
                  Complete Profile Now
                </MDButton>
                <MDButton
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    fontWeight: "bold",
                    px: 4,
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  Back to Dashboard
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
        <PropertyOwnerFooter />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={{ xs: 2, sm: 3 }}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              My Properties
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Manage and view all your property listings
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={() => navigate("/property-owner/properties/add")}
            startIcon={<Icon>add</Icon>}
            fullWidth
            sx={{ minWidth: { xs: "100%", sm: "auto" } }}
          >
            Add Property
          </MDButton>
        </MDBox>

        {message.text && (
          <MDBox
            mb={3}
            p={2}
            borderRadius="lg"
            bgColor={message.type === "success" ? "success" : "error"}
            sx={{ color: "#ffffff" }}
          >
            <MDTypography variant="body2" sx={{ color: "#ffffff !important", fontWeight: 500 }}>
              {message.text}
            </MDTypography>
          </MDBox>
        )}

        {loading ? (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="body2" color="text">
              Loading properties...
            </MDTypography>
          </MDBox>
        ) : properties.length === 0 ? (
          <MDBox p={3} borderRadius="lg" bgColor="white" shadow="sm" textAlign="center">
            <MDTypography variant="h6" mb={1}>
              No Properties Found
            </MDTypography>
            <MDTypography variant="body2" color="text">
              You haven't added any properties yet. Start by creating your first property listing.
            </MDTypography>
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            {properties.map((property) => (
              <Grid item xs={12} md={6} lg={4} key={property._id || property.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    },
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                  onClick={() => {
                    setSelectedProperty(property);
                    setDetailsDialogOpen(true);
                    setDetailsTab(0);
                  }}
                >
                  {/* Property Image */}
                  {property.images && property.images.length > 0 ? (
                    <MDBox
                      position="relative"
                      height="240px"
                      sx={{
                        backgroundImage: `url(${property.images[0]})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      {/* Image Overlay Gradient */}
                      <MDBox
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        height="60%"
                        sx={{
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)",
                        }}
                      />
                      {/* Status Badge on Image */}
                      <MDBox position="absolute" top={12} right={12}>
                        <MDBox
                          px={2.5}
                          py={1}
                          borderRadius="lg"
                          boxShadow="0 4px 12px rgba(0,0,0,0.25)"
                          sx={{
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            bgcolor:
                              property.status === "Approved"
                                ? "success.main"
                                : property.status === "Pending"
                                ? "error.main"
                                : property.status === "Rejected"
                                ? "error.dark"
                                : property.status === "Inspection" ||
                                  property.status === "Inspection_Completed"
                                ? "info.main"
                                : "grey.600",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <Icon
                              sx={{
                                fontSize: "0.875rem",
                                color: "white",
                              }}
                            >
                              {property.status === "Approved"
                                ? "check_circle"
                                : property.status === "Pending"
                                ? "schedule"
                                : property.status === "Rejected"
                                ? "cancel"
                                : property.status === "Inspection" ||
                                  property.status === "Inspection_Completed"
                                ? "search"
                                : "info"}
                            </Icon>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="white"
                              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                            >
                              {getStatusLabel(property.status, "owner", {
                                contractResponse: property.contractResponse,
                              })}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                      {/* Image Count Badge */}
                      {property.images.length > 1 && (
                        <MDBox position="absolute" bottom={12} left={12}>
                          <MDBox
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            bgColor="rgba(0,0,0,0.6)"
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                          >
                            <Icon sx={{ fontSize: "1rem", color: "white" }}>photo_library</Icon>
                            <MDTypography variant="caption" color="white" fontWeight="medium">
                              {property.images.length} photos
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      )}
                    </MDBox>
                  ) : (
                    <MDBox
                      height="220px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="grey.200"
                    >
                      <Icon sx={{ fontSize: "4rem", color: "grey.400" }}>home</Icon>
                    </MDBox>
                  )}

                  <MDBox p={3} flex={1} display="flex" flexDirection="column">
                    {/* Property Title */}
                    <MDTypography
                      variant="h5"
                      fontWeight="bold"
                      mb={0}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        color: "dark.main",
                        lineHeight: 1.3,
                      }}
                    >
                      {property.title?.en || property.title || "Untitled Property"}
                    </MDTypography>
                    {property.title?.ar && (
                      <MDTypography
                        variant="h6"
                        fontWeight="medium"
                        color="text.secondary"
                        sx={{
                          direction: "rtl",
                          textAlign: "right",
                          lineHeight: 1.3,
                        }}
                      >
                        {property.title.ar}
                      </MDTypography>
                    )}

                    {/* Property Type */}
                    <MDBox mb={2} mt={1}>
                      <MDBox
                        display="inline-flex"
                        alignItems="center"
                        px={2}
                        py={0.75}
                        borderRadius="lg"
                        bgcolor="primary.light"
                        sx={{
                          background:
                            "linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.15) 100%)",
                          border: "1px solid",
                          borderColor: "info.main",
                        }}
                      >
                        <Icon sx={{ fontSize: "1.1rem", mr: 1, color: "primary.main" }}>
                          category
                        </Icon>
                        <MDTypography variant="body2" fontWeight="bold" color="primary.main">
                          {property.propertyType
                            ? property.propertyType.charAt(0).toUpperCase() +
                              property.propertyType.slice(1).replace(/_/g, " ")
                            : "N/A"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* Property Details */}
                    <MDBox mb={2} flex={1}>
                      <MDBox display="flex" alignItems="center" mb={1}>
                        <Icon
                          fontSize="small"
                          sx={{
                            verticalAlign: "middle",
                            mr: 1,
                            color: "text.secondary",
                            fontSize: "1.1rem",
                          }}
                        >
                          location_on
                        </Icon>
                        <MDTypography
                          variant="body2"
                          color="text"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: { xs: "normal", sm: "nowrap" },
                            minWidth: 0,
                          }}
                        >
                          {property.address?.en || property.address || "N/A"}
                          {property.city?.en || property.city
                            ? `, ${property.city?.en || property.city}`
                            : ""}
                          {property.country?.en || property.country
                            ? `, ${property.country?.en || property.country}`
                            : ""}
                        </MDTypography>
                      </MDBox>
                      {(property.address?.ar || property.city?.ar || property.country?.ar) && (
                        <MDBox display="flex" alignItems="center" mb={1}>
                          <Icon
                            fontSize="small"
                            sx={{
                              verticalAlign: "middle",
                              mr: 1,
                              color: "text.secondary",
                              fontSize: "1.1rem",
                            }}
                          >
                            location_on
                          </Icon>
                          <MDTypography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                              minWidth: 0,
                              direction: "rtl",
                              textAlign: "right",
                            }}
                          >
                            {property.address?.ar || ""}
                            {property.city?.ar ? `، ${property.city.ar}` : ""}
                            {property.country?.ar ? `، ${property.country.ar}` : ""}
                          </MDTypography>
                        </MDBox>
                      )}

                      <MDBox display="flex" alignItems="center" gap={2} mb={1}>
                        <MDBox display="flex" alignItems="center">
                          <Icon
                            fontSize="small"
                            sx={{
                              verticalAlign: "middle",
                              mr: 0.5,
                              color: "text.secondary",
                              fontSize: "1.1rem",
                            }}
                          >
                            bed
                          </Icon>
                          <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
                            {property.rooms || 0}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center">
                          <Icon
                            fontSize="small"
                            sx={{
                              verticalAlign: "middle",
                              mr: 0.5,
                              color: "text.secondary",
                              fontSize: "1.1rem",
                            }}
                          >
                            bathtub
                          </Icon>
                          <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
                            {property.bathrooms || 0}
                          </MDTypography>
                        </MDBox>
                        {property.size && (
                          <MDBox display="flex" alignItems="center">
                            <Icon
                              fontSize="small"
                              sx={{
                                verticalAlign: "middle",
                                mr: 0.5,
                                color: "text.secondary",
                                fontSize: "1.1rem",
                              }}
                            >
                              square_foot
                            </Icon>
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              {property.size} sq ft
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>

                    {/* Disable Request Badge */}
                    {property.disableRequested && (
                      <MDBox mb={2}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          px={2}
                          py={1}
                          borderRadius="lg"
                          sx={{
                            background:
                              "linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.25) 100%)",
                            border: "1px solid",
                            borderColor: "info.main",
                            width: "100%",
                          }}
                        >
                          <Icon sx={{ fontSize: "1.2rem", mr: 1, color: "warning.main" }}>
                            schedule
                          </Icon>
                          <MDTypography variant="body2" fontWeight="bold" color="warning.main">
                            Disable Request Pending Approval
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    )}

                    {/* Actions */}
                    <MDBox display="flex" gap={1} flexWrap="wrap" mt="auto">
                      {property.status === "Approved" && !property.disableRequested && (
                        <MDButton
                          variant="outlined"
                          color="error"
                          size="medium"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRequestDisable(property);
                          }}
                          startIcon={<Icon>block</Icon>}
                          sx={{
                            textTransform: "none",
                            fontWeight: "bold",
                            borderRadius: 2,
                            borderWidth: 2,
                            borderColor: "info.main",
                            color: "error.main",
                            "&:hover": {
                              borderWidth: 2,
                              borderColor: "info.dark",
                              bgcolor: "info.light",
                              color: "error.dark",
                            },
                          }}
                        >
                          Request Disable
                        </MDButton>
                      )}
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>

      {/* Property Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: "100%", sm: "90vh" },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={1}
          >
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                {selectedProperty?.title?.en || selectedProperty?.title || "Property Details"}
              </MDTypography>
              {selectedProperty?.title?.ar && (
                <MDTypography
                  variant="h6"
                  fontWeight="medium"
                  color="text.secondary"
                  sx={{ direction: "rtl", textAlign: "right" }}
                >
                  {selectedProperty.title.ar}
                </MDTypography>
              )}
            </MDBox>
            <IconButton
              onClick={() => setDetailsDialogOpen(false)}
              sx={{
                "&:hover": {
                  bgcolor: "grey.100",
                },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
          {selectedProperty && (
            <MDBox>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                  value={detailsTab}
                  onChange={(e, newValue) => setDetailsTab(newValue)}
                  aria-label="property details tabs"
                >
                  <Tab label="Status & Information" />
                  <Tab
                    label={`Documents (${
                      (selectedProperty.propertyOwnerId ? 1 : 0) +
                      (selectedProperty.rentalOrOwnershipContract ? 1 : 0) +
                      (selectedProperty.powerOfAttorney ? 1 : 0) +
                      (selectedProperty.legalPapers?.length || 0) +
                      (selectedProperty.officialDocuments?.length || 0)
                    })`}
                  />
                  <Tab
                    label={`Images (${
                      (selectedProperty.images?.length || 0) +
                      (selectedProperty.buildingImages?.length || 0) +
                      (selectedProperty.livingRoomPhoto ? 1 : 0) +
                      (selectedProperty.roomPhotos?.length || 0) +
                      (selectedProperty.bathroomPhotos?.length || 0)
                    })`}
                  />
                  {selectedProperty.videos && selectedProperty.videos.length > 0 && (
                    <Tab label={`Videos (${selectedProperty.videos.length})`} />
                  )}
                </Tabs>
              </Box>

              {/* Status & Information Tab */}
              {detailsTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        background: (theme) => {
                          const statusColor = getStatusColor(selectedProperty.status);
                          const colorMap = {
                            success: theme.palette.success,
                            warning: theme.palette.warning,
                            error: theme.palette.error,
                            info: theme.palette.info,
                            dark: theme.palette.grey,
                            secondary: theme.palette.grey,
                          };
                          const palette = colorMap[statusColor] || theme.palette.grey;
                          return `linear-gradient(135deg, ${palette.main}08 0%, ${palette.dark}04 100%)`;
                        },
                        borderLeft: "4px solid",
                        borderColor: `${getStatusColor(selectedProperty.status)}.main`,
                      }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                        flexWrap="wrap"
                        gap={2}
                      >
                        <MDBox display="flex" alignItems="center" minWidth={0}>
                          <MDBox
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1.5,
                              bgcolor: `${getStatusColor(selectedProperty.status)}.main`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 2,
                              flexShrink: 0,
                            }}
                          >
                            <Icon sx={{ color: "white", fontSize: "1.75rem" }}>
                              {["Approved", "Active"].includes(selectedProperty.status)
                                ? "check_circle"
                                : ["Pending", "Request_Submission"].includes(
                                    selectedProperty.status
                                  )
                                ? "schedule"
                                : selectedProperty.status === "Rejected"
                                ? "cancel"
                                : [
                                    "Inspection",
                                    "First_Inspection",
                                    "Inspection_Offer",
                                    "Inspection_Completed",
                                    "Second_Inspection",
                                  ].includes(selectedProperty.status)
                                ? "search"
                                : "info"}
                            </Icon>
                          </MDBox>
                          <MDBox minWidth={0}>
                            <MDTypography variant="h6" fontWeight="bold">
                              Property Status
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              Current status of your property listing
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <Chip
                          label={
                            getStatusLabel(selectedProperty.status, "owner", {
                              contractResponse: selectedProperty.contractResponse,
                            }) || "Unknown"
                          }
                          color={getStatusColor(selectedProperty.status)}
                          size="medium"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            height: 32,
                            flexShrink: 0,
                          }}
                        />
                      </MDBox>
                      {["Pending", "Request_Submission"].includes(selectedProperty.status) && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Your property is currently under review. We'll notify you once it's been
                          processed.
                        </Alert>
                      )}
                      {["Inspection", "First_Inspection"].includes(selectedProperty.status) && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Your property is scheduled for inspection. An inspector will visit the
                          property soon.
                          {selectedProperty.inspectionDate && (
                            <MDTypography variant="body2" mt={1}>
                              <strong>Inspection Date:</strong>{" "}
                              {new Date(selectedProperty.inspectionDate).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </MDTypography>
                          )}
                          {selectedProperty.inspectionLocation && (
                            <MDTypography variant="body2" mt={0.5}>
                              <strong>Location:</strong> {selectedProperty.inspectionLocation}
                            </MDTypography>
                          )}
                        </Alert>
                      )}
                      {selectedProperty.status === "Inspection_Completed" && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          The inspection is complete. We’re currently reviewing it and will get back
                          to you with the results soon.{" "}
                        </Alert>
                      )}
                      {selectedProperty.status === "Inspection_Offer" && (
                        <Card
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          }}
                        >
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={1}
                            mb={2}
                          >
                            <MDTypography variant="h6" fontWeight="bold">
                              Contract
                            </MDTypography>
                            <Chip
                              label={
                                selectedProperty.contractResponse === "pending"
                                  ? "Sent"
                                  : selectedProperty.contractResponse === "approved"
                                  ? "Approved"
                                  : selectedProperty.contractResponse === "rejected"
                                  ? "Declined"
                                  : selectedProperty.contractResponse === "meeting_requested"
                                  ? "Meeting requested"
                                  : "Draft"
                              }
                              color={
                                selectedProperty.contractResponse === "pending"
                                  ? "info"
                                  : selectedProperty.contractResponse === "approved"
                                  ? "success"
                                  : selectedProperty.contractResponse === "rejected"
                                  ? "error"
                                  : "default"
                              }
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </MDBox>
                          {selectedProperty.updatedAt && (
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={1}
                            >
                              Last update:{" "}
                              {new Date(selectedProperty.updatedAt).toLocaleString("en-US", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </MDTypography>
                          )}
                          {(selectedProperty.draftContractUrl ||
                            selectedProperty.status === "Inspection_Offer") && (
                            <MDBox mb={2}>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<Icon>picture_as_pdf</Icon>}
                                onClick={async () => {
                                  const propertyId = selectedProperty._id || selectedProperty.id;
                                  const url = propertyOwnerAPI.getDraftContractUrl(propertyId);
                                  const token = localStorage.getItem("token");
                                  try {
                                    const res = await fetch(url, {
                                      headers: token ? { Authorization: `Bearer ${token}` } : {},
                                      redirect: "follow",
                                    });
                                    if (!res.ok) {
                                      setMessage({
                                        type: "error",
                                        text: "Failed to load contract. It may not be available yet.",
                                      });
                                      return;
                                    }
                                    const blob = await res.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    window.open(blobUrl, "_blank", "noopener,noreferrer");
                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                                  } catch (err) {
                                    setMessage({
                                      type: "error",
                                      text: err.message || "Failed to load contract.",
                                    });
                                  }
                                }}
                              >
                                View / Download contract (PDF)
                              </MDButton>
                            </MDBox>
                          )}
                          {selectedProperty.contractResponse === "pending" ? (
                            <>
                              <MDTypography variant="body2" color="text.secondary" mb={2}>
                                Review the draft rental listing agreement and choose an action
                                below.
                              </MDTypography>
                              <MDBox display="flex" flexWrap="wrap" gap={1}>
                                <MDButton
                                  variant="gradient"
                                  color="success"
                                  size="small"
                                  startIcon={<Icon>check_circle</Icon>}
                                  disabled={submitting}
                                  onClick={async () => {
                                    try {
                                      setSubmitting(true);
                                      setMessage({ type: "", text: "" });
                                      const res = await propertyOwnerAPI.submitContractResponse(
                                        selectedProperty._id || selectedProperty.id,
                                        "approved"
                                      );
                                      setMessage({
                                        type: "success",
                                        text:
                                          res.message ||
                                          "Offer approved. Property is now in Contracting.",
                                      });
                                      const list = await propertyOwnerAPI.getMyProperties();
                                      setProperties(list.data || list.properties || []);
                                      const updated = (list.data || list.properties || []).find(
                                        (p) =>
                                          (p._id || p.id) ===
                                          (selectedProperty._id || selectedProperty.id)
                                      );
                                      if (updated) setSelectedProperty(updated);
                                    } catch (err) {
                                      setMessage({
                                        type: "error",
                                        text: err.message || "Failed to submit response.",
                                      });
                                    } finally {
                                      setSubmitting(false);
                                    }
                                  }}
                                >
                                  Approve
                                </MDButton>
                                <MDButton
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<Icon>cancel</Icon>}
                                  disabled={submitting}
                                  onClick={async () => {
                                    try {
                                      setSubmitting(true);
                                      setMessage({ type: "", text: "" });
                                      const res = await propertyOwnerAPI.submitContractResponse(
                                        selectedProperty._id || selectedProperty.id,
                                        "rejected"
                                      );
                                      setMessage({
                                        type: "success",
                                        text: res.message || "Offer declined.",
                                      });
                                      const list = await propertyOwnerAPI.getMyProperties();
                                      setProperties(list.data || list.properties || []);
                                      const updated = (list.data || list.properties || []).find(
                                        (p) =>
                                          (p._id || p.id) ===
                                          (selectedProperty._id || selectedProperty.id)
                                      );
                                      if (updated) setSelectedProperty(updated);
                                    } catch (err) {
                                      setMessage({
                                        type: "error",
                                        text: err.message || "Failed to submit response.",
                                      });
                                    } finally {
                                      setSubmitting(false);
                                    }
                                  }}
                                >
                                  Decline
                                </MDButton>
                                <MDButton
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  startIcon={<Icon>event</Icon>}
                                  disabled={submitting}
                                  onClick={async () => {
                                    try {
                                      setSubmitting(true);
                                      setMessage({ type: "", text: "" });
                                      const res = await propertyOwnerAPI.submitContractResponse(
                                        selectedProperty._id || selectedProperty.id,
                                        "meeting_requested"
                                      );
                                      setMessage({
                                        type: "success",
                                        text:
                                          res.message ||
                                          "Meeting requested. Our team will contact you.",
                                      });
                                      const list = await propertyOwnerAPI.getMyProperties();
                                      setProperties(list.data || list.properties || []);
                                      const updated = (list.data || list.properties || []).find(
                                        (p) =>
                                          (p._id || p.id) ===
                                          (selectedProperty._id || selectedProperty.id)
                                      );
                                      if (updated) setSelectedProperty(updated);
                                    } catch (err) {
                                      setMessage({
                                        type: "error",
                                        text: err.message || "Failed to submit response.",
                                      });
                                    } finally {
                                      setSubmitting(false);
                                    }
                                  }}
                                >
                                  Request Meeting
                                </MDButton>
                              </MDBox>
                            </>
                          ) : (
                            <MDTypography variant="body2" color="text.secondary">
                              {selectedProperty.contractResponse === "approved"
                                ? "You approved the offer. Property is now in Contracting."
                                : selectedProperty.contractResponse === "meeting_requested"
                                ? "You requested a meeting. Our team will contact you."
                                : "You declined the offer."}
                            </MDTypography>
                          )}
                        </Card>
                      )}
                      {selectedProperty.status === "Rejected" && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          Your property has been rejected. Please review the feedback and resubmit
                          if needed.
                        </Alert>
                      )}
                      {["Approved", "Active"].includes(selectedProperty.status) && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          Congratulations! Your property has been approved and is now live.
                        </Alert>
                      )}
                      {selectedProperty.status === "Second_Inspection" && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Your property is scheduled for a second inspection. An inspector will
                          visit soon.
                          {selectedProperty.inspectionDate && (
                            <MDTypography variant="body2" mt={1}>
                              <strong>Inspection Date:</strong>{" "}
                              {new Date(selectedProperty.inspectionDate).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </MDTypography>
                          )}
                        </Alert>
                      )}
                      {["Preparation", "Contracting", "Legalization", "Photo_Shooting"].includes(
                        selectedProperty.status
                      ) && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Your property is being prepared for listing. We'll notify you when it's
                          ready.
                        </Alert>
                      )}
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" mb={2.5}>
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>home</Icon>
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Property Information
                        </MDTypography>
                      </MDBox>

                      <MDBox mb={2}>
                        <MDBox display="flex" alignItems="center" mb={1}>
                          <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                            title
                          </Icon>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                          >
                            Title (English)
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="body1" fontWeight="medium" sx={{ pl: 4 }}>
                          {selectedProperty.title?.en || selectedProperty.title || "N/A"}
                        </MDTypography>
                      </MDBox>

                      {selectedProperty.title?.ar && (
                        <MDBox mb={2}>
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              title
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Title (Arabic)
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="body1"
                            fontWeight="medium"
                            sx={{ pl: 4, direction: "rtl", textAlign: "right" }}
                          >
                            {selectedProperty.title.ar}
                          </MDTypography>
                        </MDBox>
                      )}

                      <MDBox mb={2}>
                        <MDBox display="flex" alignItems="center" mb={1}>
                          <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                            category
                          </Icon>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                          >
                            Property Type
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="body1" sx={{ pl: 4 }}>
                          {selectedProperty.propertyType
                            ? selectedProperty.propertyType.charAt(0).toUpperCase() +
                              selectedProperty.propertyType.slice(1).replace(/_/g, " ")
                            : "N/A"}
                        </MDTypography>
                      </MDBox>

                      <MDBox mb={2}>
                        <MDBox display="flex" alignItems="center" mb={1}>
                          <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                            location_on
                          </Icon>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                          >
                            Address (English)
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="body1" sx={{ pl: 4 }}>
                          {selectedProperty.address?.en || selectedProperty.address || "N/A"}
                          {selectedProperty.city?.en || selectedProperty.city
                            ? `, ${selectedProperty.city?.en || selectedProperty.city}`
                            : ""}
                          {selectedProperty.country?.en || selectedProperty.country
                            ? `, ${selectedProperty.country?.en || selectedProperty.country}`
                            : ""}
                        </MDTypography>
                      </MDBox>

                      {(selectedProperty.address?.ar ||
                        selectedProperty.city?.ar ||
                        selectedProperty.country?.ar) && (
                        <MDBox mb={2}>
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              location_on
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Address (Arabic)
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="body1"
                            sx={{ pl: 4, direction: "rtl", textAlign: "right" }}
                          >
                            {selectedProperty.address?.ar || selectedProperty.address || ""}
                            {selectedProperty.city?.ar || selectedProperty.city
                              ? `، ${selectedProperty.city?.ar || selectedProperty.city}`
                              : ""}
                            {selectedProperty.country?.ar || selectedProperty.country
                              ? `، ${selectedProperty.country?.ar || selectedProperty.country}`
                              : ""}
                          </MDTypography>
                        </MDBox>
                      )}

                      <MDBox
                        display="flex"
                        gap={2}
                        flexWrap="wrap"
                        p={2}
                        borderRadius={2}
                        sx={{ bgcolor: "grey.50", mt: 2 }}
                      >
                        {selectedProperty.rooms && (
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ fontSize: "1.2rem", mr: 1, color: "text.secondary" }}>
                              bed
                            </Icon>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Rooms
                              </MDTypography>
                              <MDTypography variant="body1" fontWeight="bold">
                                {selectedProperty.rooms}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        )}
                        {selectedProperty.bathrooms && (
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ fontSize: "1.2rem", mr: 1, color: "text.secondary" }}>
                              bathroom
                            </Icon>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Bathrooms
                              </MDTypography>
                              <MDTypography variant="body1" fontWeight="bold">
                                {selectedProperty.bathrooms}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        )}
                        {selectedProperty.size && (
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ fontSize: "1.2rem", mr: 1, color: "text.secondary" }}>
                              square_foot
                            </Icon>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Size
                              </MDTypography>
                              <MDTypography variant="body1" fontWeight="bold">
                                {selectedProperty.size} sq ft
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        )}
                        {selectedProperty.price && selectedProperty.status === "Approved" && (
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ fontSize: "1.2rem", mr: 1, color: "success.main" }}>
                              attach_money
                            </Icon>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Price/Night
                              </MDTypography>
                              <MDTypography variant="body1" fontWeight="bold" color="success.main">
                                {selectedProperty.price.toLocaleString()} EGP
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        )}
                        {selectedProperty.insurancePrice &&
                          selectedProperty.status === "Approved" && (
                            <MDBox display="flex" alignItems="center">
                              <Icon sx={{ fontSize: "1.2rem", mr: 1, color: "info.main" }}>
                                shield
                              </Icon>
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Insurance
                                </MDTypography>
                                <MDTypography variant="body1" fontWeight="bold" color="info.main">
                                  {selectedProperty.insurancePrice.toLocaleString()} EGP
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          )}
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" mb={2.5}>
                        <MDBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>info</Icon>
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Additional Details
                        </MDTypography>
                      </MDBox>

                      {(selectedProperty.description?.en || selectedProperty.description) && (
                        <MDBox mb={3}>
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              description
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Description (English)
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              pl: 4,
                              p: 2,
                              borderRadius: 1,
                              bgcolor: "grey.50",
                              lineHeight: 1.6,
                            }}
                          >
                            {selectedProperty.description?.en || selectedProperty.description}
                          </MDTypography>
                        </MDBox>
                      )}

                      {selectedProperty.description?.ar && (
                        <MDBox mb={3}>
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              description
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Description (Arabic)
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              direction: "rtl",
                              textAlign: "right",
                              pl: 4,
                              p: 2,
                              borderRadius: 1,
                              bgcolor: "grey.50",
                              lineHeight: 1.6,
                            }}
                          >
                            {selectedProperty.description.ar}
                          </MDTypography>
                        </MDBox>
                      )}

                      {selectedProperty.amenities && selectedProperty.amenities.length > 0 && (
                        <MDBox mb={3}>
                          <MDBox display="flex" alignItems="center" mb={1.5}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              star
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Amenities
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" gap={1} flexWrap="wrap" sx={{ pl: 4 }}>
                            {selectedProperty.amenities.map((amenity, index) => {
                              const amenityLabel =
                                typeof amenity === "object" && amenity.en
                                  ? amenity.en
                                  : typeof amenity === "string"
                                  ? amenity
                                  : "";
                              return (
                                <Chip
                                  key={index}
                                  label={amenityLabel}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: "primary.main",
                                    "&:hover": {
                                      bgcolor: "primary.lighter",
                                    },
                                  }}
                                />
                              );
                            })}
                          </MDBox>
                        </MDBox>
                      )}

                      {/* WiFi Information */}
                      {(selectedProperty.wifiName || selectedProperty.wifiPassword) && (
                        <MDBox mb={3}>
                          <MDBox display="flex" alignItems="center" mb={1.5}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              wifi
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              WiFi Information
                            </MDTypography>
                          </MDBox>
                          <MDBox sx={{ pl: 4 }}>
                            {selectedProperty.wifiName && (
                              <MDBox mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  mb={0.5}
                                >
                                  WiFi Name
                                </MDTypography>
                                <MDTypography variant="body2" fontWeight="medium">
                                  {selectedProperty.wifiName}
                                </MDTypography>
                              </MDBox>
                            )}
                            {selectedProperty.wifiPassword && (
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  mb={0.5}
                                >
                                  WiFi Password
                                </MDTypography>
                                <MDTypography variant="body2" fontWeight="medium">
                                  {selectedProperty.wifiPassword}
                                </MDTypography>
                              </MDBox>
                            )}
                          </MDBox>
                        </MDBox>
                      )}

                      {/* Utilities */}
                      {(selectedProperty.electricityMeterType ||
                        selectedProperty.waterMeterType) && (
                        <MDBox mb={3}>
                          <MDBox display="flex" alignItems="center" mb={1.5}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              bolt
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Utilities
                            </MDTypography>
                          </MDBox>
                          <MDBox sx={{ pl: 4 }}>
                            {selectedProperty.electricityMeterType && (
                              <MDBox mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  mb={0.5}
                                >
                                  Electricity Meter
                                </MDTypography>
                                <MDTypography variant="body2" fontWeight="medium">
                                  {selectedProperty.electricityMeterType}
                                </MDTypography>
                              </MDBox>
                            )}
                            {selectedProperty.waterMeterType && (
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  mb={0.5}
                                >
                                  Water Meter
                                </MDTypography>
                                <MDTypography variant="body2" fontWeight="medium">
                                  {selectedProperty.waterMeterType}
                                </MDTypography>
                              </MDBox>
                            )}
                          </MDBox>
                        </MDBox>
                      )}

                      {(selectedProperty.inspectionNotes?.en ||
                        selectedProperty.inspectionNotes?.ar ||
                        selectedProperty.inspectionFindings?.additionalNotes) && (
                        <MDBox>
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              note
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Inspection Notes
                            </MDTypography>
                          </MDBox>
                          {selectedProperty.inspectionNotes?.en && (
                            <MDBox mb={1.5}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mb={0.5}
                                sx={{ pl: 4 }}
                              >
                                English
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  pl: 4,
                                  p: 2,
                                  borderRadius: 1,
                                  bgcolor: "info.lighter",
                                  borderLeft: "3px solid",
                                  borderColor: "info.main",
                                  lineHeight: 1.6,
                                }}
                              >
                                {selectedProperty.inspectionNotes.en}
                              </MDTypography>
                            </MDBox>
                          )}
                          {selectedProperty.inspectionNotes?.ar && (
                            <MDBox mb={1.5}>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mb={0.5}
                                sx={{ pl: 4 }}
                              >
                                Arabic
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  direction: "rtl",
                                  textAlign: "right",
                                  pl: 4,
                                  p: 2,
                                  borderRadius: 1,
                                  bgcolor: "info.lighter",
                                  borderLeft: "3px solid",
                                  borderColor: "info.main",
                                  lineHeight: 1.6,
                                }}
                              >
                                {selectedProperty.inspectionNotes.ar}
                              </MDTypography>
                            </MDBox>
                          )}
                          {selectedProperty.inspectionFindings?.additionalNotes &&
                            !selectedProperty.inspectionNotes && (
                              <MDTypography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  pl: 4,
                                  p: 2,
                                  borderRadius: 1,
                                  bgcolor: "info.lighter",
                                  borderLeft: "3px solid",
                                  borderColor: "info.main",
                                  lineHeight: 1.6,
                                }}
                              >
                                {selectedProperty.inspectionFindings.additionalNotes}
                              </MDTypography>
                            )}
                        </MDBox>
                      )}
                    </Card>
                  </Grid>

                  {/* Room Photos Section */}
                  {(selectedProperty.livingRoomPhoto ||
                    (selectedProperty.roomPhotos && selectedProperty.roomPhotos.length > 0) ||
                    (selectedProperty.bathroomPhotos &&
                      selectedProperty.bathroomPhotos.length > 0)) && (
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" mb={2.5}>
                          <MDBox
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              bgcolor: "grey.200",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 2,
                            }}
                          >
                            <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>
                              photo_library
                            </Icon>
                          </MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            Room & Bathroom Photos
                          </MDTypography>
                        </MDBox>

                        <Grid container spacing={2}>
                          {/* Living Room Photo */}
                          {selectedProperty.livingRoomPhoto && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Paper
                                elevation={2}
                                sx={{
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  "&:hover": {
                                    boxShadow: 4,
                                  },
                                }}
                              >
                                <MDBox
                                  component="img"
                                  src={selectedProperty.livingRoomPhoto}
                                  alt="Living Room"
                                  sx={{
                                    width: "100%",
                                    height: "200px",
                                    objectFit: "cover",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    window.open(selectedProperty.livingRoomPhoto, "_blank")
                                  }
                                />
                                <MDBox p={1.5}>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Living Room
                                  </MDTypography>
                                </MDBox>
                              </Paper>
                            </Grid>
                          )}

                          {/* Room Photos */}
                          {selectedProperty.roomPhotos &&
                            selectedProperty.roomPhotos.length > 0 && (
                              <>
                                {selectedProperty.roomPhotos.map((roomPhoto, index) => {
                                  // Handle both object format {roomNumber, photoUrl} and string format
                                  const photoUrl =
                                    typeof roomPhoto === "object" && roomPhoto.photoUrl
                                      ? roomPhoto.photoUrl
                                      : typeof roomPhoto === "string"
                                      ? roomPhoto
                                      : null;
                                  const roomNumber =
                                    typeof roomPhoto === "object" && roomPhoto.roomNumber
                                      ? roomPhoto.roomNumber
                                      : index + 1;

                                  if (!photoUrl) return null;

                                  return (
                                    <Grid item xs={12} sm={6} md={4} key={`room-${index}`}>
                                      <Paper
                                        elevation={2}
                                        sx={{
                                          borderRadius: 2,
                                          overflow: "hidden",
                                          "&:hover": {
                                            boxShadow: 4,
                                          },
                                        }}
                                      >
                                        <MDBox
                                          component="img"
                                          src={photoUrl}
                                          alt={`Room ${roomNumber}`}
                                          sx={{
                                            width: "100%",
                                            height: "200px",
                                            objectFit: "cover",
                                            cursor: "pointer",
                                          }}
                                          onClick={() => window.open(photoUrl, "_blank")}
                                          onError={(e) => {
                                            console.error("Failed to load room photo:", photoUrl);
                                            e.target.style.display = "none";
                                          }}
                                        />
                                        <MDBox p={1.5}>
                                          <MDTypography variant="body2" fontWeight="medium">
                                            Room {roomNumber}
                                          </MDTypography>
                                        </MDBox>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </>
                            )}

                          {/* Bathroom Photos */}
                          {selectedProperty.bathroomPhotos &&
                            selectedProperty.bathroomPhotos.length > 0 && (
                              <>
                                {selectedProperty.bathroomPhotos.map((bathroomPhoto, index) => {
                                  // Handle both object format {bathroomNumber, photoUrl} and string format
                                  const photoUrl =
                                    typeof bathroomPhoto === "object" && bathroomPhoto.photoUrl
                                      ? bathroomPhoto.photoUrl
                                      : typeof bathroomPhoto === "string"
                                      ? bathroomPhoto
                                      : null;
                                  const bathroomNumber =
                                    typeof bathroomPhoto === "object" &&
                                    bathroomPhoto.bathroomNumber
                                      ? bathroomPhoto.bathroomNumber
                                      : index + 1;

                                  if (!photoUrl) return null;

                                  return (
                                    <Grid item xs={12} sm={6} md={4} key={`bathroom-${index}`}>
                                      <Paper
                                        elevation={2}
                                        sx={{
                                          borderRadius: 2,
                                          overflow: "hidden",
                                          "&:hover": {
                                            boxShadow: 4,
                                          },
                                        }}
                                      >
                                        <MDBox
                                          component="img"
                                          src={photoUrl}
                                          alt={`Bathroom ${bathroomNumber}`}
                                          sx={{
                                            width: "100%",
                                            height: "200px",
                                            objectFit: "cover",
                                            cursor: "pointer",
                                          }}
                                          onClick={() => window.open(photoUrl, "_blank")}
                                          onError={(e) => {
                                            console.error(
                                              "Failed to load bathroom photo:",
                                              photoUrl
                                            );
                                            e.target.style.display = "none";
                                          }}
                                        />
                                        <MDBox p={1.5}>
                                          <MDTypography variant="body2" fontWeight="medium">
                                            Bathroom {bathroomNumber}
                                          </MDTypography>
                                        </MDBox>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </>
                            )}
                        </Grid>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Documents Tab */}
              {detailsTab === 1 && (
                <MDBox>
                  <Grid container spacing={3}>
                    {/* Property Owner Uploaded Documents */}
                    <Grid item xs={12}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Your Uploaded Documents
                      </MDTypography>
                    </Grid>

                    {/* Check if property owner has uploaded any documents */}
                    {!selectedProperty.propertyOwnerId &&
                    !selectedProperty.rentalOrOwnershipContract &&
                    !selectedProperty.powerOfAttorney &&
                    (!selectedProperty.legalPapers || selectedProperty.legalPapers.length === 0) ? (
                      <Grid item xs={12}>
                        <MDBox
                          textAlign="center"
                          py={4}
                          sx={{
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            border: "2px dashed",
                            borderColor: "divider",
                          }}
                        >
                          <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}>
                            description
                          </Icon>
                          <MDTypography variant="body1" color="text.secondary" mb={0.5}>
                            No Documents Uploaded
                          </MDTypography>
                          <MDTypography variant="body2" color="text.secondary">
                            You haven't uploaded any documents for this property yet.
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    ) : (
                      <>
                        {/* Property Owner ID */}
                        {selectedProperty.propertyOwnerId && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                  boxShadow: 4,
                                },
                              }}
                            >
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                              >
                                <MDBox display="flex" alignItems="center">
                                  <Icon sx={{ color: "primary.main", mr: 1 }}>badge</Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Property Owner ID
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                              <MDButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                startIcon={<Icon>visibility</Icon>}
                                onClick={() =>
                                  viewPdf(
                                    fixPdfUrl(selectedProperty.propertyOwnerId),
                                    "Property Owner ID"
                                  )
                                }
                              >
                                View Document
                              </MDButton>
                            </Paper>
                          </Grid>
                        )}

                        {/* Rental or Ownership Contract */}
                        {selectedProperty.rentalOrOwnershipContract && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                  boxShadow: 4,
                                },
                              }}
                            >
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                              >
                                <MDBox display="flex" alignItems="center">
                                  <Icon sx={{ color: "success.main", mr: 1 }}>description</Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Rental/Ownership Contract
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                              <MDButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                startIcon={<Icon>visibility</Icon>}
                                onClick={() =>
                                  viewPdf(
                                    fixPdfUrl(selectedProperty.rentalOrOwnershipContract),
                                    "Rental or Ownership Contract"
                                  )
                                }
                              >
                                View Document
                              </MDButton>
                            </Paper>
                          </Grid>
                        )}

                        {/* Power of Attorney */}
                        {selectedProperty.powerOfAttorney && (
                          <Grid item xs={12} sm={6} md={4}>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                  boxShadow: 4,
                                },
                              }}
                            >
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                              >
                                <MDBox display="flex" alignItems="center">
                                  <Icon sx={{ color: "warning.main", mr: 1 }}>gavel</Icon>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    Power of Attorney
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                              <MDButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                startIcon={<Icon>visibility</Icon>}
                                onClick={() =>
                                  viewPdf(
                                    fixPdfUrl(selectedProperty.powerOfAttorney),
                                    "Power of Attorney"
                                  )
                                }
                              >
                                View Document
                              </MDButton>
                            </Paper>
                          </Grid>
                        )}

                        {/* Legacy Legal Papers (if any) */}
                        {selectedProperty.legalPapers &&
                          selectedProperty.legalPapers.length > 0 && (
                            <>
                              {selectedProperty.legalPapers.map((docUrl, index) => (
                                <Grid item xs={12} sm={6} md={4} key={`legal-${index}`}>
                                  <Paper
                                    elevation={2}
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: "1px solid",
                                      borderColor: "divider",
                                      "&:hover": {
                                        boxShadow: 4,
                                      },
                                    }}
                                  >
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      mb={1}
                                    >
                                      <MDBox display="flex" alignItems="center">
                                        <Icon sx={{ color: "info.main", mr: 1 }}>description</Icon>
                                        <MDTypography variant="body2" fontWeight="medium">
                                          Legal Paper {index + 1}
                                        </MDTypography>
                                      </MDBox>
                                    </MDBox>
                                    <MDButton
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      fullWidth
                                      startIcon={<Icon>visibility</Icon>}
                                      onClick={() =>
                                        viewPdf(fixPdfUrl(docUrl), `Legal Paper ${index + 1}`)
                                      }
                                    >
                                      View Document
                                    </MDButton>
                                  </Paper>
                                </Grid>
                              ))}
                            </>
                          )}
                      </>
                    )}

                    {/* Official Documents (Uploaded by Admin) */}
                    <Grid item xs={12} sx={{ mt: 3 }}>
                      <Divider sx={{ my: 2 }} />
                      <MDBox display="flex" alignItems="center" mb={2}>
                        <MDTypography variant="h6" fontWeight="bold" mr={1}>
                          Official Documents
                        </MDTypography>
                        <Chip
                          label="Admin Uploaded"
                          size="small"
                          color="info"
                          sx={{ height: 24 }}
                        />
                      </MDBox>
                      {!selectedProperty.officialDocuments ||
                      selectedProperty.officialDocuments.length === 0 ? (
                        <MDBox
                          textAlign="center"
                          py={4}
                          sx={{
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            border: "2px dashed",
                            borderColor: "divider",
                          }}
                        >
                          <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}>
                            folder_open
                          </Icon>
                          <MDTypography variant="body1" color="text.secondary" mb={0.5}>
                            No Official Documents Yet
                          </MDTypography>
                          <MDTypography variant="body2" color="text.secondary">
                            Official documents will be uploaded by the admin after property
                            approval.
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <Grid container spacing={2}>
                          {selectedProperty.officialDocuments.map((docUrl, index) => (
                            <Grid item xs={12} sm={6} md={4} key={`official-${index}`}>
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  "&:hover": {
                                    boxShadow: 4,
                                  },
                                }}
                              >
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  mb={1}
                                >
                                  <MDBox display="flex" alignItems="center">
                                    <Icon sx={{ color: "error.main", mr: 1 }}>description</Icon>
                                    <MDTypography variant="body2" fontWeight="medium">
                                      Official Document {index + 1}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                                <MDButton
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  fullWidth
                                  startIcon={<Icon>visibility</Icon>}
                                  onClick={() =>
                                    viewPdf(fixPdfUrl(docUrl), `Official Document ${index + 1}`)
                                  }
                                >
                                  View Document
                                </MDButton>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </MDBox>
              )}

              {/* Images Tab */}
              {detailsTab === 2 && (
                <MDBox>
                  {(!selectedProperty.images || selectedProperty.images.length === 0) &&
                  (!selectedProperty.buildingImages ||
                    selectedProperty.buildingImages.length === 0) &&
                  !selectedProperty.livingRoomPhoto &&
                  (!selectedProperty.roomPhotos || selectedProperty.roomPhotos.length === 0) &&
                  (!selectedProperty.bathroomPhotos ||
                    selectedProperty.bathroomPhotos.length === 0) ? (
                    <MDBox textAlign="center" py={6}>
                      <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>image</Icon>
                      <MDTypography variant="h6" color="text.secondary" mb={1}>
                        No Images Found
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        No images have been uploaded for this property.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <Grid container spacing={2}>
                      {/* Property Images */}
                      {selectedProperty.images && selectedProperty.images.length > 0 && (
                        <>
                          <Grid item xs={12}>
                            <MDTypography variant="h6" fontWeight="medium" mb={2}>
                              Property Images
                            </MDTypography>
                          </Grid>
                          {selectedProperty.images.map((imageUrl, index) => (
                            <Grid item xs={12} sm={6} md={4} key={`property-${index}`}>
                              <Paper
                                elevation={2}
                                sx={{
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  "&:hover": {
                                    boxShadow: 4,
                                  },
                                }}
                              >
                                <MDBox
                                  component="img"
                                  src={imageUrl}
                                  alt={`Property image ${index + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: "200px",
                                    objectFit: "cover",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => window.open(imageUrl, "_blank")}
                                  onError={(e) => {
                                    console.error("Failed to load image:", imageUrl);
                                    e.target.style.display = "none";
                                  }}
                                />
                                <MDBox p={1.5}>
                                  <MDTypography variant="caption" color="text.secondary">
                                    Property Image {index + 1}
                                  </MDTypography>
                                </MDBox>
                              </Paper>
                            </Grid>
                          ))}
                        </>
                      )}

                      {/* Living Room Photo */}
                      {selectedProperty.livingRoomPhoto && (
                        <>
                          <Grid
                            item
                            xs={12}
                            sx={{ mt: selectedProperty.images?.length > 0 ? 2 : 0 }}
                          >
                            <MDTypography variant="h6" fontWeight="medium" mb={2}>
                              Living Room
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Paper
                              elevation={2}
                              sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                "&:hover": {
                                  boxShadow: 4,
                                },
                              }}
                            >
                              <MDBox
                                component="img"
                                src={selectedProperty.livingRoomPhoto}
                                alt="Living Room"
                                sx={{
                                  width: "100%",
                                  height: "200px",
                                  objectFit: "cover",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  window.open(selectedProperty.livingRoomPhoto, "_blank")
                                }
                                onError={(e) => {
                                  console.error(
                                    "Failed to load living room photo:",
                                    selectedProperty.livingRoomPhoto
                                  );
                                  e.target.style.display = "none";
                                }}
                              />
                              <MDBox p={1.5}>
                                <MDTypography variant="body2" fontWeight="medium">
                                  Living Room
                                </MDTypography>
                              </MDBox>
                            </Paper>
                          </Grid>
                        </>
                      )}

                      {/* Room Photos */}
                      {selectedProperty.roomPhotos && selectedProperty.roomPhotos.length > 0 && (
                        <>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              mt:
                                selectedProperty.images?.length > 0 ||
                                selectedProperty.livingRoomPhoto
                                  ? 2
                                  : 0,
                            }}
                          >
                            <MDTypography variant="h6" fontWeight="medium" mb={2}>
                              Room Photos
                            </MDTypography>
                          </Grid>
                          {selectedProperty.roomPhotos.map((roomPhoto, index) => {
                            // Handle both object format {roomNumber, photoUrl} and string format
                            const photoUrl =
                              typeof roomPhoto === "object" ? roomPhoto.photoUrl : roomPhoto;
                            const roomNumber =
                              typeof roomPhoto === "object" ? roomPhoto.roomNumber : index + 1;

                            if (!photoUrl) return null;

                            return (
                              <Grid item xs={12} sm={6} md={4} key={`room-${index}`}>
                                <Paper
                                  elevation={2}
                                  sx={{
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    "&:hover": {
                                      boxShadow: 4,
                                    },
                                  }}
                                >
                                  <MDBox
                                    component="img"
                                    src={photoUrl}
                                    alt={`Room ${roomNumber}`}
                                    sx={{
                                      width: "100%",
                                      height: "200px",
                                      objectFit: "cover",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => window.open(photoUrl, "_blank")}
                                    onError={(e) => {
                                      console.error("Failed to load room photo:", photoUrl);
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <MDBox p={1.5}>
                                    <MDTypography variant="body2" fontWeight="medium">
                                      Room {roomNumber}
                                    </MDTypography>
                                  </MDBox>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </>
                      )}

                      {/* Bathroom Photos */}
                      {selectedProperty.bathroomPhotos &&
                        selectedProperty.bathroomPhotos.length > 0 && (
                          <>
                            <Grid
                              item
                              xs={12}
                              sx={{
                                mt:
                                  selectedProperty.images?.length > 0 ||
                                  selectedProperty.livingRoomPhoto ||
                                  selectedProperty.roomPhotos?.length > 0
                                    ? 2
                                    : 0,
                              }}
                            >
                              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                                Bathroom Photos
                              </MDTypography>
                            </Grid>
                            {selectedProperty.bathroomPhotos.map((bathroomPhoto, index) => {
                              // Handle both object format {bathroomNumber, photoUrl} and string format
                              const photoUrl =
                                typeof bathroomPhoto === "object"
                                  ? bathroomPhoto.photoUrl
                                  : bathroomPhoto;
                              const bathroomNumber =
                                typeof bathroomPhoto === "object"
                                  ? bathroomPhoto.bathroomNumber
                                  : index + 1;

                              if (!photoUrl) return null;

                              return (
                                <Grid item xs={12} sm={6} md={4} key={`bathroom-${index}`}>
                                  <Paper
                                    elevation={2}
                                    sx={{
                                      borderRadius: 2,
                                      overflow: "hidden",
                                      "&:hover": {
                                        boxShadow: 4,
                                      },
                                    }}
                                  >
                                    <MDBox
                                      component="img"
                                      src={photoUrl}
                                      alt={`Bathroom ${bathroomNumber}`}
                                      sx={{
                                        width: "100%",
                                        height: "200px",
                                        objectFit: "cover",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => window.open(photoUrl, "_blank")}
                                      onError={(e) => {
                                        console.error("Failed to load bathroom photo:", photoUrl);
                                        e.target.style.display = "none";
                                      }}
                                    />
                                    <MDBox p={1.5}>
                                      <MDTypography variant="body2" fontWeight="medium">
                                        Bathroom {bathroomNumber}
                                      </MDTypography>
                                    </MDBox>
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </>
                        )}

                      {/* Building Images */}
                      {selectedProperty.buildingImages &&
                        selectedProperty.buildingImages.length > 0 && (
                          <>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                                Building Images (Outside)
                              </MDTypography>
                            </Grid>
                            {selectedProperty.buildingImages.map((imageUrl, index) => (
                              <Grid item xs={12} sm={6} md={4} key={`building-${index}`}>
                                <Paper
                                  elevation={2}
                                  sx={{
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    "&:hover": {
                                      boxShadow: 4,
                                    },
                                  }}
                                >
                                  <MDBox
                                    component="img"
                                    src={imageUrl}
                                    alt={`Building image ${index + 1}`}
                                    sx={{
                                      width: "100%",
                                      height: "200px",
                                      objectFit: "cover",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => window.open(imageUrl, "_blank")}
                                    onError={(e) => {
                                      console.error("Failed to load building image:", imageUrl);
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <MDBox p={1.5}>
                                    <MDTypography variant="caption" color="text.secondary">
                                      Building Image {index + 1}
                                    </MDTypography>
                                  </MDBox>
                                </Paper>
                              </Grid>
                            ))}
                          </>
                        )}
                    </Grid>
                  )}
                </MDBox>
              )}

              {/* Videos Tab */}
              {detailsTab === 3 &&
                selectedProperty.videos &&
                selectedProperty.videos.length > 0 && (
                  <MDBox>
                    <Grid container spacing={2}>
                      {selectedProperty.videos.map((videoUrl, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper
                            elevation={2}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <MDBox
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              mb={1}
                            >
                              <MDBox display="flex" alignItems="center">
                                <Icon sx={{ color: "error.main", mr: 1 }}>videocam</Icon>
                                <MDTypography variant="body2" fontWeight="medium">
                                  Video {index + 1}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDButton
                              variant="outlined"
                              color="primary"
                              size="small"
                              fullWidth
                              startIcon={<Icon>play_arrow</Icon>}
                              href={videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Video
                            </MDButton>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: 1, borderColor: "divider" }}>
          <MDButton
            onClick={() => setDetailsDialogOpen(false)}
            variant="outlined"
            color="secondary"
            startIcon={<Icon>close</Icon>}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Disable Request Dialog */}
      <Dialog
        open={disableDialogOpen}
        onClose={() => !submitting && setDisableDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request to Disable Property</DialogTitle>
        <DialogContent>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text" mb={2}>
              You are requesting to disable the following property. This request will be sent to
              Super Admin for approval.
            </MDTypography>
            {selectedProperty && (
              <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  {selectedProperty.title?.en || selectedProperty.title || "N/A"}
                </MDTypography>
                {selectedProperty.title?.ar && (
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="medium"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    {selectedProperty.title.ar}
                  </MDTypography>
                )}
                <MDTypography variant="body2" color="text">
                  {selectedProperty.address?.en || selectedProperty.address || "N/A"},{" "}
                  {selectedProperty.city?.en || selectedProperty.city || ""}
                </MDTypography>
                {(selectedProperty.address?.ar || selectedProperty.city?.ar) && (
                  <MDTypography
                    variant="body2"
                    color="text.secondary"
                    sx={{ direction: "rtl", textAlign: "right" }}
                  >
                    {selectedProperty.address?.ar || ""}, {selectedProperty.city?.ar || ""}
                  </MDTypography>
                )}
              </MDBox>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Disable Request"
              placeholder="Please provide a reason for disabling this property..."
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              disabled={submitting}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setDisableDialogOpen(false);
              setDisableReason("");
            }}
            disabled={submitting}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitDisableRequest}
            variant="gradient"
            color="error"
            disabled={submitting || !disableReason.trim()}
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* PDF Lightbox Viewer */}
      <Dialog
        open={pdfLightboxOpen}
        onClose={() => setPdfLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="bold">
              {pdfLightboxTitle}
            </MDTypography>
            <MDBox display="flex" gap={1}>
              {!useGoogleViewer && (
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<Icon>refresh</Icon>}
                  onClick={() => setUseGoogleViewer(true)}
                >
                  Use Google Viewer
                </MDButton>
              )}
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<Icon>download</Icon>}
                onClick={() => {
                  if (pdfLightboxUrl) {
                    const link = document.createElement("a");
                    link.href = pdfLightboxUrl;
                    link.download = `${pdfLightboxTitle}.pdf`;
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
              >
                Download
              </MDButton>
              <IconButton onClick={() => setPdfLightboxOpen(false)}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, height: "75vh", position: "relative", overflow: "hidden" }}
        >
          {pdfLightboxUrl && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: "75vh",
                position: "relative",
              }}
            >
              {useGoogleViewer ? (
                /* Google Docs Viewer as fallback */
                <Box
                  component="iframe"
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                    pdfLightboxUrl
                  )}&embedded=true`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: "75vh",
                    border: "none",
                  }}
                  title={pdfLightboxTitle}
                />
              ) : (
                /* Try iframe first for Cloudinary PDFs */
                <Box
                  component="iframe"
                  src={`${pdfLightboxUrl}#view=FitH`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: "75vh",
                    border: "none",
                  }}
                  title={pdfLightboxTitle}
                  onError={() => {
                    // If iframe fails, try Google Viewer
                    setUseGoogleViewer(true);
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <PropertyOwnerFooter />
    </DashboardLayout>
  );
}

export default MyProperties;
